import { type ReactElement, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { chartColors, severityChartColors } from 'constants/colors';

import { SEVERITY_HEAT_ROWS } from './runAnalysisSelectors';

import type { ChartData, ChartOptions } from 'chart.js';
import type { IssueTrendSeries } from './runAnalysisSelectors';

import styles from './scss/TargetedMarketing.module.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

interface IWatchTowerIssueTrend {
  series: IssueTrendSeries,
}

function WatchTowerIssueTrend({ series }: IWatchTowerIssueTrend): ReactElement {
  const { t } = useTranslation();
  const { charcoal, culturedPearl } = chartColors;

  const data: ChartData<'line'> = useMemo(() => ({
    labels: series.buckets,
    datasets: SEVERITY_HEAT_ROWS.map((severity) => ({
      label: severity,
      data: series.counts[severity],
      borderColor: severityChartColors[severity],
      backgroundColor: severityChartColors[severity],
      pointBackgroundColor: severityChartColors[severity],
      tension: 0,
      spanGaps: true,
      pointRadius: 3,
      borderWidth: 2,
    })),
  }), [series]);

  const options: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'bottom',
        align: 'start',
        labels: {
          usePointStyle: true,
          boxWidth: 12,
          padding: 16,
          color: charcoal,
        },
      },
      tooltip: {
        callbacks: {
          title: (items) => items[0]?.label || '',
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: t('targetedMarketing.issueTrendTime'),
          color: charcoal,
        },
        ticks: {
          color: charcoal,
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: charcoal,
          precision: 0,
        },
        grid: {
          color: culturedPearl,
        },
        title: {
          display: true,
          text: t('targetedMarketing.issueTrendCount'),
          color: charcoal,
        },
      },
    },
  }), [charcoal, culturedPearl, t]);

  return (
    <div className={styles.trendChart}>
      <Line data={data} options={options} />
    </div>
  );
}

export default WatchTowerIssueTrend;
